import { useMemo } from "react";
import {
  Bar,
  type BarShapeProps,
  CartesianGrid,
  BarChart as RechartsBarChart,
  Rectangle,
  XAxis,
  YAxis,
} from "recharts";
import type { AxisDomain } from "recharts/types/util/types";
import { RotatedCategoryAxisTick } from "@/components/ui/bar-chart/categoryAxisTicks";
import {
  ChartLegend,
  ChartLegendContent,
  ChartLegendSeriesContent,
} from "@/components/ui/ChartLegend";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  chartColorCssVarKey,
} from "@/components/ui/ChartTooltip";
import {
  CHART_VALUE_AXIS_TOP_MARGIN_PX,
  ColumnChartValueYAxis,
  formatChartValueAxisTick,
  useColumnChartValueAxis,
} from "@/components/ui/columnChartValueAxis";
import { useReportTheme } from "@/components/ui/report/ReportThemeProvider";
import { cn } from "@/lib/utils";
import {
  formatCategoryAxisTick,
  resolveCategoryAxisLayout,
} from "@/lib/utils/chartAxisTickFormatter";
import { resolveCustomSegmentChartConfig } from "@/lib/utils/chartUtils";
import { stackedBarRadius } from "@/lib/utils/stackedBarRadius";

export type BarDatum = Record<string, string | number | undefined>;

export type BarSeriesItem = {
  key: string;
  label?: string;
};

export type BarChartViewProps = {
  data: BarDatum[];
  chartConfig: ChartConfig;
  /** Category axis field (months, labels, etc.). */
  xAxisKey: string;
  /** One or more numeric series to render as bars. */
  series: BarSeriesItem[];
  /**
   * Recharts `layout`: `horizontal` = vertical columns (category on X); `vertical` = horizontal bars (category on Y).
   * @default 'horizontal'
   */
  layout?: "horizontal" | "vertical";
  /** When true, all series share one stack (stacked bars). */
  stacked?: boolean;
  title?: string | null;
  description?: string | null;
  height?: number | null;
  className?: string;
  /** When set to a positive number, category tick labels are truncated to this length; omit for full labels. */
  xAxisTickChars?: number;
  /** Omit to auto-rotate long or dense category labels at -45°. */
  rotateXAxisTick?: boolean;
  /** @default true when {@link series} has more than one entry */
  showLegend?: boolean;
  /**
   * When false, the category label (e.g. month) appears above series rows. @default false
   */
  hideTooltipLabel?: boolean;
  /**
   * Corner radius. Columns: top corners only (single bar or top segment of a stack). Horizontal bars: value end only.
   * Stacked: only the top column segment / outer horizontal segment is rounded; base sits square on the axis.
   * @default 4
   */
  radius?: number;
  /**
   * Recharts bar enter animation. Default off: animated bars + frequent parent re-renders (e.g. json-render chat)
   * can hit Recharts `JavascriptAnimate` / `handleAnimationEnd` update loops ("Maximum update depth exceeded").
   */
  isAnimationActive?: boolean;
  accessibilityLayer?: boolean;
  /** Per-category bar fills keyed by {@link xAxisKey} value (single-series charts). */
  categoryColors?: Record<string, string>;
};

/** Recharts props so every category tick renders on the column-chart x-axis */
const SHOW_ALL_CATEGORY_TICKS = {
  interval: 0 as const,
  minTickGap: 0 as const,
};

/** Default Recharts bottom margin for non-rotated category axes. */
const DEFAULT_AXIS_MARGIN_PX = 12;

const BarChartEmpty = () => (
  <div className="flex w-full min-w-0 items-center justify-center rounded-md border border-dashed border-muted-foreground/25 py-4 text-xxs text-muted-foreground">
    No data to display
  </div>
);

/** Adds per-row `fill` for category-coloured single-series bars (shape + tooltip). */
export const attachCategoryBarFills = (
  data: BarDatum[],
  xAxisKey: string,
  categoryColors: Record<string, string>,
  fallbackFill: string,
): BarDatum[] =>
  data.map((row) => {
    const category = String(row[xAxisKey] ?? "");
    const fill = categoryColors[category] ?? fallbackFill;
    return { ...row, fill };
  });

const createCategoryBarShape = (fallbackFill: string) => {
  const CategoryBarShape = (props: BarShapeProps) => {
    const payload = props.payload as BarDatum | undefined;
    const fill =
      typeof payload?.fill === "string" ? payload.fill : fallbackFill;
    return <Rectangle {...props} fill={fill} />;
  };
  return CategoryBarShape;
};

const BarChartView = ({
  data,
  chartConfig,
  xAxisKey,
  series,
  layout = "horizontal",
  stacked = false,
  title = null,
  description = null,
  height = null,
  className,
  xAxisTickChars,
  rotateXAxisTick,
  showLegend: showLegendProp,
  hideTooltipLabel = false,
  radius = 4,
  isAnimationActive = false,
  accessibilityLayer = true,
  categoryColors,
}: BarChartViewProps) => {
  const inReportContext = useReportTheme() != null;
  const seriesList =
    series.length > 0
      ? series.map((s) => ({
          key: s.key,
          label: String(chartConfig[s.key]?.label ?? s.label ?? s.key),
        }))
      : [];

  const hasValues =
    data.length > 0 &&
    seriesList.length > 0 &&
    data.some((row) =>
      seriesList.some((s) => {
        const v = row[s.key];
        const n = typeof v === "number" ? v : Number(v);
        return !Number.isNaN(n);
      }),
    );

  const valueKeys = useMemo(() => series.map((s) => s.key), [series]);
  const singleSeriesKey = seriesList[0]?.key;
  const canUseCategoryColors =
    !stacked && seriesList.length === 1 && singleSeriesKey != null;

  const {
    resolvedChartConfig,
    segmentLegendConfig: categoryLegendConfig,
    hasSegmentLegend: hasCategoryLegend,
    legendItems: categoryLegendItems,
    hasCustomSegmentColors: hasCustomCategoryColors,
  } = useMemo(() => {
    if (!canUseCategoryColors || singleSeriesKey == null) {
      return {
        resolvedChartConfig: chartConfig,
        segmentLegendConfig: {},
        hasSegmentLegend: false,
        legendItems: [],
        hasCustomSegmentColors: false,
      };
    }

    const result = resolveCustomSegmentChartConfig({
      baseConfig: chartConfig,
      segmentColors: categoryColors,
      data,
      segmentKey: xAxisKey,
      defaultSeriesKey: singleSeriesKey,
    });

    if (
      result.hasCustomSegmentColors &&
      !result.hasSegmentLegend &&
      result.resolvedChartConfig[singleSeriesKey]?.label == null &&
      seriesList[0]?.label != null
    ) {
      return {
        ...result,
        resolvedChartConfig: {
          ...result.resolvedChartConfig,
          [singleSeriesKey]: {
            ...result.resolvedChartConfig[singleSeriesKey],
            label: seriesList[0].label,
          },
        },
      };
    }

    return result;
  }, [
    canUseCategoryColors,
    chartConfig,
    categoryColors,
    data,
    xAxisKey,
    singleSeriesKey,
    seriesList,
  ]);

  const categoryBarShape = useMemo(() => {
    if (!hasCustomCategoryColors || singleSeriesKey == null) {
      return undefined;
    }
    const fallbackFill = `var(--color-${chartColorCssVarKey(singleSeriesKey)})`;
    return createCategoryBarShape(fallbackFill);
  }, [hasCustomCategoryColors, singleSeriesKey]);

  const chartData = useMemo(() => {
    if (
      !hasCustomCategoryColors ||
      categoryColors == null ||
      singleSeriesKey == null
    ) {
      return data;
    }
    const fallbackFill = `var(--color-${chartColorCssVarKey(singleSeriesKey)})`;
    return attachCategoryBarFills(data, xAxisKey, categoryColors, fallbackFill);
  }, [
    data,
    hasCustomCategoryColors,
    categoryColors,
    xAxisKey,
    singleSeriesKey,
  ]);

  const useSeriesLegendContent = hasCategoryLegend;

  const showLegend =
    showLegendProp ??
    (inReportContext || hasCustomCategoryColors || seriesList.length > 1);
  const isColumnChart = layout === "horizontal";
  const valueAxis = useColumnChartValueAxis(data, {
    autoMinValue: false,
    valueKeys,
  });

  if (!hasValues) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-1.5">
        {title != null && title !== "" ? (
          <p className="m-0 text-xs font-medium text-foreground">{title}</p>
        ) : null}
        <BarChartEmpty />
      </div>
    );
  }

  const stackId = stacked ? "bar-stack" : undefined;
  const n = seriesList.length;
  const categoryLabels = isColumnChart
    ? chartData.map((row) => String(row[xAxisKey] ?? ""))
    : [];
  const {
    shouldRotate: autoRotate,
    margins: autoMargins,
    rotatedMargins,
  } = resolveCategoryAxisLayout(categoryLabels);
  const shouldRotateXAxisTick =
    isColumnChart && (rotateXAxisTick ?? autoRotate);
  const axisMargins = shouldRotateXAxisTick
    ? autoRotate
      ? autoMargins
      : rotatedMargins
    : { bottom: DEFAULT_AXIS_MARGIN_PX, left: DEFAULT_AXIS_MARGIN_PX };
  const categoryTickFormatter = formatCategoryAxisTick(
    shouldRotateXAxisTick ? undefined : xAxisTickChars,
  );
  const useExternalLegend =
    showLegend && (shouldRotateXAxisTick || useSeriesLegendContent);
  const plotHeight = height ?? 280;
  const chartHeight = shouldRotateXAxisTick
    ? plotHeight + axisMargins.bottom - DEFAULT_AXIS_MARGIN_PX
    : height;

  return (
    <div className="flex w-full min-w-0 flex-col gap-1.5">
      {title != null && title !== "" ? (
        <p className="m-0 text-xs font-medium text-foreground">{title}</p>
      ) : null}
      {description != null && description !== "" ? (
        <p className="m-0 text-xs text-muted-foreground">{description}</p>
      ) : null}
      <ChartContainer
        config={resolvedChartConfig}
        className={cn(
          "mx-auto w-full justify-center",
          !shouldRotateXAxisTick && "aspect-video",
          chartHeight == null && "max-h-[240px]",
          chartHeight != null &&
            !shouldRotateXAxisTick &&
            "max-h-[min(280px,100%)]",
          className,
        )}
        style={
          chartHeight != null
            ? { height: chartHeight, minHeight: chartHeight }
            : undefined
        }
      >
        <RechartsBarChart
          accessibilityLayer={accessibilityLayer}
          data={chartData}
          layout={layout}
          margin={{
            top: CHART_VALUE_AXIS_TOP_MARGIN_PX,
            left: axisMargins.left,
            right: 12,
            ...(shouldRotateXAxisTick && { bottom: axisMargins.bottom }),
          }}
        >
          {isColumnChart ? (
            <CartesianGrid vertical={false} />
          ) : (
            <CartesianGrid horizontal={false} />
          )}
          {isColumnChart ? (
            <XAxis
              dataKey={xAxisKey}
              tickLine={false}
              axisLine={false}
              tickMargin={shouldRotateXAxisTick ? 4 : 10}
              tickFormatter={categoryTickFormatter}
              {...(shouldRotateXAxisTick
                ? {
                    ...SHOW_ALL_CATEGORY_TICKS,
                    tick: (props) => (
                      <RotatedCategoryAxisTick
                        {...props}
                        tickFormatter={categoryTickFormatter}
                      />
                    ),
                  }
                : {})}
            />
          ) : (
            <XAxis
              type="number"
              tickLine={false}
              axisLine={false}
              allowDecimals
              domain={valueAxis.yAxisDomain as AxisDomain}
              tickFormatter={formatChartValueAxisTick}
            />
          )}
          {isColumnChart ? (
            <ColumnChartValueYAxis
              calculatedYAxisWidth={valueAxis.calculatedYAxisWidth}
              yAxisDomain={valueAxis.yAxisDomain}
              onWidthChange={valueAxis.handleYAxisLabelWidthChange}
            />
          ) : (
            <YAxis
              dataKey={xAxisKey}
              type="category"
              tickLine={false}
              axisLine={false}
              width={56}
              tickFormatter={formatCategoryAxisTick(xAxisTickChars)}
            />
          )}
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                hideLabel={hideTooltipLabel}
                indicator="dot"
              />
            }
          />
          {showLegend && !useExternalLegend ? (
            <ChartLegend
              verticalAlign="bottom"
              content={
                <ChartLegendContent className="flex-wrap gap-x-2 gap-y-0.5 pt-1.5 text-foreground dark:text-gray-300" />
              }
            />
          ) : null}
          {seriesList.map((s, index) => {
            const fill = `var(--color-${chartColorCssVarKey(s.key)})`;
            const barRadius: [number, number, number, number] = stacked
              ? stackedBarRadius(index, n, radius, isColumnChart)
              : isColumnChart
                ? [radius, radius, 0, 0]
                : [0, radius, radius, 0];
            return (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.label}
                fill={fill}
                radius={barRadius}
                stackId={stackId}
                isAnimationActive={isAnimationActive}
                shape={
                  !stacked && seriesList.length === 1
                    ? categoryBarShape
                    : undefined
                }
              />
            );
          })}
        </RechartsBarChart>
      </ChartContainer>
      {useExternalLegend ? (
        <ChartLegendSeriesContent
          config={
            hasCategoryLegend ? categoryLegendConfig : resolvedChartConfig
          }
          series={hasCategoryLegend ? categoryLegendItems : seriesList}
          className="flex-wrap gap-x-2 gap-y-0.5 pt-1.5 text-foreground dark:text-gray-300"
        />
      ) : null}
    </div>
  );
};

export default BarChartView;
